import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type {
  SpokeProfile,
  SpokeEngagement,
  SpokeSyncLog,
  ProfileType,
} from "@/types/social-listening-v2";

export const useSpokeProfiles = (profileType?: ProfileType) => {
  return useQuery({
    queryKey: ["sl-v2-profiles-spoke", profileType ?? "all"],
    queryFn: async (): Promise<SpokeProfile[]> => {
      let q = (supabase as any)
        .from("social_listening_profiles")
        .select("*")
        .eq("is_active", true)
        .order("profile_name", { ascending: true });
      if (profileType) q = q.eq("profile_type", profileType);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as SpokeProfile[];
    },
  });
};

export const useSpokeEngagements = (limit: number = 200) => {
  return useQuery({
    queryKey: ["sl-v2-engagements-spoke", limit],
    queryFn: async (): Promise<SpokeEngagement[]> => {
      const { data, error } = await (supabase as any)
        .from("social_listening_engagements")
        .select("*")
        .order("synced_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data || []) as SpokeEngagement[];
    },
  });
};

export const useLatestSyncLog = () => {
  return useQuery({
    queryKey: ["sl-v2-sync-log"],
    queryFn: async (): Promise<SpokeSyncLog | null> => {
      const { data, error } = await (supabase as any)
        .from("trigify_sync_log")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as SpokeSyncLog | null;
    },
  });
};

export const useTriggerSpokeSync = (clientSlug: string = "carr_bio") => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await (supabase as any).rpc("sync_social_listening_from_hub", {
        p_client_slug: clientSlug,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sl-v2-profiles-spoke"] });
      qc.invalidateQueries({ queryKey: ["sl-v2-engagements-spoke"] });
      qc.invalidateQueries({ queryKey: ["sl-v2-sync-log"] });
    },
  });
};
